import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, 
  AlertTriangle, 
  Eye, 
  Users, 
  Key,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  Search,
  Filter,
  RefreshCw
} from 'lucide-react';
import { auditLogService, AuditLog, SecurityEvent } from '@/services/security/auditLogService';
import { permissionService } from '@/services/security/permissionService';
import { formatDate, formatDateTime } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export function SecurityDashboard() {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [securityAnalytics, setSecurityAnalytics] = useState<any>(null);
  const [permissionAnalytics, setPermissionAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('week');
  const { toast } = useToast();

  useEffect(() => {
    loadSecurityData();
  }, [dateRange]);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      
      const startDate = getStartDate(dateRange);
      
      const [
        auditLogsData,
        securityEventsData,
        securityAnalyticsData,
        permissionAnalyticsData
      ] = await Promise.all([
        auditLogService.getAuditLogs({
          start_date: startDate,
          limit: 100
        }),
        auditLogService.getSecurityEvents({
          start_date: startDate,
          limit: 50
        }),
        auditLogService.getSecurityAnalytics(dateRange as any),
        permissionService.getPermissionAnalytics()
      ]);

      setAuditLogs(auditLogsData.logs);
      setSecurityEvents(securityEventsData.events);
      setSecurityAnalytics(securityAnalyticsData);
      setPermissionAnalytics(permissionAnalyticsData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load security data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = (range: string): string => {
    const now = new Date();
    switch (range) {
      case 'day':
        now.setDate(now.getDate() - 1);
        break;
      case 'week':
        now.setDate(now.getDate() - 7);
        break;
      case 'month':
        now.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        now.setFullYear(now.getFullYear() - 1);
        break;
    }
    return now.toISOString();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failure': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default: return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const generateComplianceReport = async () => {
    try {
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      
      await auditLogService.generateComplianceReport({
        report_type: 'gdpr',
        period_start: startDate.toISOString(),
        period_end: new Date().toISOString()
      });

      toast({
        title: "Success",
        description: "Compliance report generation started"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate compliance report",
        variant: "destructive"
      });
    }
  };

  const filteredAuditLogs = auditLogs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeverity = severityFilter === 'all' || log.severity === severityFilter;
    
    return matchesSearch && matchesSeverity;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading security data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Security Dashboard</h2>
          <p className="text-muted-foreground">
            Monitor security events, audit trails, and compliance
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={generateComplianceReport}>
            <Download className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
          <Button onClick={loadSecurityData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="flex items-center gap-4">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Last Day</SelectItem>
            <SelectItem value="week">Last Week</SelectItem>
            <SelectItem value="month">Last Month</SelectItem>
            <SelectItem value="year">Last Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="audit-logs">Audit Logs</TabsTrigger>
          <TabsTrigger value="security-events">Security Events</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Security Metrics */}
          {securityAnalytics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Actions</p>
                      <p className="text-2xl font-bold">{securityAnalytics.total_actions}</p>
                    </div>
                    <Activity className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active Users</p>
                      <p className="text-2xl font-bold">{securityAnalytics.total_users}</p>
                    </div>
                    <Users className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Security Incidents</p>
                      <p className="text-2xl font-bold text-red-600">{securityAnalytics.security_incidents}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">High Risk Events</p>
                      <p className="text-2xl font-bold text-orange-600">{securityAnalytics.high_risk_events}</p>
                    </div>
                    <Shield className="w-8 h-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Activity Breakdown */}
          <div className="grid gap-6 md:grid-cols-2">
            {securityAnalytics && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Actions by Severity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(securityAnalytics.actions_by_severity || {}).map(([severity, count]) => (
                        <div key={severity} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={getSeverityColor(severity)}>
                              {severity}
                            </Badge>
                          </div>
                          <span className="font-medium">{count as number}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Active Users</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(securityAnalytics.top_users || []).slice(0, 5).map((user: any) => (
                        <div key={user.user_id} className="flex items-center justify-between">
                          <span className="text-sm">{user.user_name}</span>
                          <Badge variant="outline">{user.action_count} actions</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit-logs" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search audit logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Audit Logs */}
          <div className="space-y-3">
            {filteredAuditLogs.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Eye className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No audit logs found</h3>
                  <p className="text-muted-foreground">
                    {searchTerm ? 'Try adjusting your search filters' : 'No audit logs available for the selected period'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredAuditLogs.map(log => (
                <Card key={log.id} className="transition-all hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        {getStatusIcon(log.status)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge className={getSeverityColor(log.severity)}>
                              {log.severity}
                            </Badge>
                            <span className="text-sm font-medium">{log.user_name}</span>
                            <span className="text-sm text-muted-foreground">→</span>
                            <span className="text-sm">{log.action}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatDateTime(log.created_at)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>Resource: {log.resource_type}</span>
                          {log.resource_name && <span>({log.resource_name})</span>}
                          {log.ip_address && <span>IP: {log.ip_address}</span>}
                        </div>
                        
                        {(log.old_values || log.new_values) && (
                          <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                            {log.old_values && (
                              <div>
                                <span className="font-medium">Before:</span>{' '}
                                {JSON.stringify(log.old_values, null, 2)}
                              </div>
                            )}
                            {log.new_values && (
                              <div>
                                <span className="font-medium">After:</span>{' '}
                                {JSON.stringify(log.new_values, null, 2)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="security-events" className="space-y-4">
          <div className="space-y-3">
            {securityEvents.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No security events</h3>
                  <p className="text-muted-foreground">
                    No security events found for the selected period
                  </p>
                </CardContent>
              </Card>
            ) : (
              securityEvents.map(event => (
                <Card key={event.id} className="transition-all hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          event.risk_score >= 70 ? 'bg-red-100' : 
                          event.risk_score >= 50 ? 'bg-orange-100' : 'bg-blue-100'
                        }`}>
                          <Shield className={`w-4 h-4 ${
                            event.risk_score >= 70 ? 'text-red-600' : 
                            event.risk_score >= 50 ? 'text-orange-600' : 'text-blue-600'
                          }`} />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{event.event_type}</Badge>
                            <span className="text-sm font-medium">Risk Score: {event.risk_score}</span>
                            {event.blocked && (
                              <Badge variant="destructive">Blocked</Badge>
                            )}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatDateTime(event.created_at)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                          {event.user_email && <span>User: {event.user_email}</span>}
                          <span>IP: {event.ip_address}</span>
                          {event.location && (
                            <span>Location: {event.location.city}, {event.location.country}</span>
                          )}
                        </div>
                        
                        {Object.keys(event.details).length > 0 && (
                          <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                            <span className="font-medium">Details:</span>{' '}
                            {JSON.stringify(event.details, null, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-4">
          {permissionAnalytics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Users</p>
                      <p className="text-2xl font-bold">{permissionAnalytics.total_users}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active Roles</p>
                      <p className="text-2xl font-bold">{permissionAnalytics.total_roles}</p>
                    </div>
                    <Key className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Requests</p>
                      <p className="text-2xl font-bold text-orange-600">{permissionAnalytics.pending_requests}</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Approval Rate</p>
                      <p className="text-2xl font-bold">{permissionAnalytics.request_approval_rate}%</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}